import React from 'react';
import { ClockIcon, StoreIcon, TagIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Meter } from '../ui/Meter';
import type { CampaGift, GiftTypeConfig } from '../../types/campagift';
import { STATUS_LABEL, daysUntilExpiry, koboToNaira, statusTone } from '../../utils/campagift';
interface GiftTileProps {
  gift: CampaGift;
  type: GiftTypeConfig;
  remainingKobo: number;
  status: CampaGift['status'];
  selected: boolean;
  onSelect: () => void;
}

/**
 * Remaining value is the primary figure — it is what the holder can actually
 * spend. Face value, scope and expiry are supporting information and are
 * deliberately smaller.
 */
export function GiftTile({
  gift,
  type,
  remainingKobo,
  status,
  selected,
  onSelect
}: GiftTileProps) {
  const days = daysUntilExpiry(gift);
  const expiringSoon = days !== null && days > 0 && days <= 14;
  const spent = gift.faceValueKobo - remainingKobo;
  return <button type="button" onClick={onSelect} aria-current={selected} className={`w-full rounded-card border bg-surface px-5 py-4 text-left transition-colors duration-150 ease-out ${selected ? 'border-brand-700' : 'border-line hover:border-line-strong'}`} data-id="element-802" data-mp="2r5q87v89rhq9">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-803" data-mp="20a96dtat4avn">
        <div className="min-w-0" data-id="element-804" data-mp="1vc7ohbp4ahlr">
          <p className="text-body-sm font-medium text-ink-muted" data-id="element-805" data-mp="1vfbywqt6e9kv">{type.name}</p>
          <p className="cz-num mt-1 text-h3 font-semibold text-ink" data-id="element-806" data-mp="1qf7guwcmd6sj">
            {koboToNaira(remainingKobo)}
          </p>
          <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-807" data-mp="1lld3sbrirq2f">
            of {koboToNaira(gift.faceValueKobo)} · {gift.publicReference}
          </p>
        </div>
        <Badge tone={statusTone(status)} plain={status === 'available'} data-id="element-808" data-mp="1co2ma8o3z4p2">
          {STATUS_LABEL[status]}
        </Badge>
      </div>

      {spent > 0 && remainingKobo > 0 && <div className="mt-3" data-id="element-809" data-mp="12pd8os38w6em">
          <Meter label="Redeemed so far" value={Math.round(spent / gift.faceValueKobo * 100)} trailing={koboToNaira(spent)} height="thin" data-id="element-810" data-mp="12qeu9qhw0bal" />
        </div>}

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-line pt-3 text-caption text-ink-muted" data-id="element-811" data-mp="j2jqquoiyofd">
        <li className="flex items-center gap-1.5" data-id="element-812" data-mp="o13d2w716289">
          {type.scope === 'merchant' ? <StoreIcon aria-hidden className="h-4 w-4 shrink-0" data-id="element-813" data-mp="1brha714h2r9p" /> : <TagIcon aria-hidden className="h-4 w-4 shrink-0" data-id="element-814" data-mp="1btjy9fm2eabs" />}
          {type.eligibleCategoryIds === 'all' ? 'All participating merchants' : type.eligibleCategoryIds.join(', ')}
        </li>
        <li className="flex items-center gap-1.5" data-id="element-815" data-mp="16zz9me6d2sp9">
          <ClockIcon aria-hidden className="h-4 w-4 shrink-0" data-id="element-816" data-mp="16yeb6dgxepm4" />
          {gift.expiresAt ? expiringSoon ? `Expires in ${days} day${days === 1 ? '' : 's'}` : `Expires ${gift.expiresAt}` : 'No expiry'}
        </li>
      </ul>
    </button>;
}